import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FIRM, FIRM_ADDRESS, PENINSULA_ADDRESS, faqSchema } from "@/lib/firm";

const STEPS = [
  {
    t: "A workspace is created",
    d: "Once the contract is unconditional, your solicitor opens a PEXA workspace for the property and invites the other side's solicitor and both banks into it. Everything from that point happens in one shared online file.",
  },
  {
    t: "You verify your identity",
    d: "You complete a verification of identity — in person, or remotely by video with an approved provider — and sign a client authorisation that lets us sign the transfer electronically on your behalf.",
  },
  {
    t: "Documents are prepared and signed",
    d: "The transfer is prepared in the workspace, duty is assessed and paid, and any mortgage or release of mortgage is added by the banks. Each party digitally signs their part.",
  },
  {
    t: "Funds are balanced",
    d: "The financial settlement schedule is built: loan advance, deposit release, payout of the seller's mortgage, rates and water adjustments, and the balance to the seller. Both solicitors must agree it to the cent before settlement can proceed.",
  },
  {
    t: "Settlement runs at the booked time",
    d: "At the appointed time PEXA checks everyone is ready, moves the money through the Reserve Bank, and lodges the transfer and mortgage with Titles Queensland automatically. It takes minutes.",
  },
  {
    t: "Keys and confirmation",
    d: "We tell you the moment it is done so the agent can release the keys, then notify council, the water authority and any body corporate of the change of ownership.",
  },
];

const FAQS = [
  {
    q: "What is PEXA?",
    a: "PEXA (Property Exchange Australia) is the electronic conveyancing platform used for almost all Queensland property settlements. Instead of solicitors and bank officers meeting to swap cheques and paper titles, the transfer is signed digitally, funds move through the Reserve Bank, and the dealing is lodged with Titles Queensland automatically at the settlement moment.",
  },
  {
    q: "Do I have to attend settlement?",
    a: "No. Queensland residential settlements are electronic, so there is nothing to attend. You verify your identity, sign a client authorisation, and your solicitor runs the settlement in the workspace. Most clients are at work when their property settles.",
  },
  {
    q: "What time does PEXA settlement happen?",
    a: "Settlements are booked in a time slot during business hours, commonly between 10am and 4pm. The slot is agreed between the two solicitors and the banks. Funds typically clear into the seller's account the same day when the receiving bank is part of the electronic network.",
  },
  {
    q: "Can an electronic settlement still fail?",
    a: "Yes, though far less often than paper settlement did. The usual causes are practical rather than legal: the incoming bank has not certified the loan funds, a payout figure has changed, a party has not signed in the workspace, or the balancing figures do not match. Because everything is visible in the workspace beforehand, most of these are caught a day or two out — which is exactly why we build the schedule early rather than on the morning.",
  },
  {
    q: "What does PEXA cost?",
    a: "PEXA charges a transaction fee per dealing, which is part of the disbursements on your settlement statement along with searches and Titles Registry lodgement fees. It is passed on at cost. Our professional fee is fixed and quoted before we start.",
  },
  {
    q: "Can I settle from interstate or overseas?",
    a: "Yes. Identity verification can be done remotely through an approved provider, documents are signed electronically, and the settlement itself is online. We act regularly for buyers and executors who never set foot in Queensland during the transaction.",
  },
];

export const Route = createFileRoute("/pexa-settlement")({
  head: () => ({
    meta: [
      { title: fitTitle(`PEXA Settlement Explained | Electronic Settlement QLD | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("How PEXA electronic settlement works in Queensland: the workspace, identity verification, funds balancing, settlement time, delays and costs — explained by a solicitor."),
      },
      { property: "og:title", content: fitTitle(`PEXA Settlement Explained | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("A plain-English walk-through of electronic property settlement in Queensland, step by step."),
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://coastsidelaw.com.au/pexa-settlement" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/pexa-settlement" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: PexaSettlementPage,
});

function PexaSettlementPage() {
  return (
    <>
      <PageHero
        eyebrow="Conveyancing · settlement"
        title="PEXA settlement, explained"
        lead="Queensland property settlements are electronic. There is no cheque handover and nothing for you to attend — but plenty that has to line up behind the scenes. Here is what actually happens between the contract going unconditional and the keys being released."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">The six steps of an electronic settlement</h2>
        <ol className="mt-6 space-y-4">
          {STEPS.map((s, i) => (
            <li key={s.t} className="rounded-sm border border-border bg-card p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-accent">
                Step {i + 1}
              </p>
              <h3 className="mt-1 font-serif text-xl text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </li>
          ))}
        </ol>

        <h2 id="settlement-times" className="mt-14 font-serif text-3xl text-foreground">
          PEXA settlement times
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          PEXA settlements in Queensland are booked into a time slot on a business day, usually
          between about 10am and 4pm Brisbane time. The solicitors for the buyer and seller agree the
          slot with the banks a few days before settlement. The settlement itself takes a few
          minutes once the slot arrives.
        </p>
        <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
          <li>
            <strong className="text-foreground">Seller's funds:</strong> usually arrive the same
            afternoon if the seller's bank is on the electronic network.
          </li>
          <li>
            <strong className="text-foreground">Keys:</strong> the agent releases them once we confirm
            settlement is complete, often within the hour.
          </li>
          <li>
            <strong className="text-foreground">Titles registration:</strong> the transfer is lodged
            automatically at settlement, and the title usually updates within a few days.
          </li>
          <li>
            <strong className="text-foreground">Late slots:</strong> a late-afternoon slot leaves little
            time to fix a problem that day, so we ask for a morning or midday slot where we can.
          </li>
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Why settlements still get delayed</h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          The platform is reliable; the people feeding it are the variable. A lender that certifies
          funds at the last minute, a payout figure that moves because of a direct debit, an unsigned
          client authorisation, a body corporate certificate that arrived late, rates adjustments
          that do not agree — these are what push a settlement into the next day, and every one of
          them is avoidable with the workspace built early. We prepare and balance well ahead of the
          booked slot rather than on the morning, and you hear from us if something is at risk.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Electronic settlement also does not reduce the legal work in front of it. The contract
          conditions, disclosure, title issues and adjustments are where money is won and lost. The
          settlement itself is the last five minutes.
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Where we settle</h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          We settle Queensland-wide from two offices — {FIRM_ADDRESS} and {PENINSULA_ADDRESS}, both
          by appointment. Because settlement is electronic, where you live makes no difference to how
          the transaction runs.
        </p>
        <ul className="mt-5 space-y-2 text-sm">
          <li>
            <Link to="/conveyancing-sunshine-coast" className="font-semibold text-primary underline">
              Sunshine Coast conveyancing
            </Link>
          </li>
          <li>
            <Link to="/conveyancing/$suburb" params={{ suburb: "caloundra" }} className="font-semibold text-primary underline">
              Caloundra conveyancing
            </Link>
          </li>
          <li>
            <Link
              to="/conveyancing/$suburb"
              params={{ suburb: "redcliffe" }}
              className="font-semibold text-primary underline"
            >
              Redcliffe and Moreton Bay conveyancing
            </Link>
          </li>
          <li>
            <Link to="/what-does-a-conveyancer-do-qld" className="font-semibold text-primary underline">
              What a conveyancer does in Queensland
            </Link>
          </li>
          <li>
            <Link to="/stamp-duty-calculator-qld" className="font-semibold text-primary underline">
              Queensland stamp duty calculator
            </Link>
          </li>
          <li>
            <Link to="/fees" className="font-semibold text-primary underline">
              Conveyancing fees and settlement costs
            </Link>
          </li>
        </ul>

        <PeopleStrip slugs={["erin-wilkinson", "michael-klein"]} heading="Who runs your settlement" />

        <h2 className="mt-14 font-serif text-3xl text-foreground">PEXA settlement questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>

        <div className="mt-14">
          <EnquiryForm heading="Book your settlement with a solicitor" />
        </div>
      </section>

      <CtaBand
        title="Contract signed, settlement to run?"
        body={`Send it through and we will open the workspace, diarise every date and confirm a fixed fee. Call ${FIRM.phone}.`}
      />
      <div className="h-20" />
    </>
  );
}
